import React, { useState, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Divider,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Savings as SavingsIcon,
  Star as StarIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { transactionService, SmartRecommendationResponse, SavingsAnalysis, SpendingCategory } from '../services/transactions';
import { cardService, UserCard } from '../services/cards';
import { AuthContext } from '../App';

const CATEGORIES = [
  'GROCERIES',
  'DINING',
  'FUEL',
  'TRAVEL',
  'SHOPPING',
  'ENTERTAINMENT',
  'UTILITIES',
  'ONLINE_SHOPPING',
  'OTHER',
];

const SmartRecommendations: React.FC = () => {
  const [amount, setAmount] = useState<string>('');
  const [category, setCategory] = useState<string>('GROCERIES');
  const [transactionLocation, setTransactionLocation] = useState<'local' | 'international'>('local');
  const [rewardPreference, setRewardPreference] = useState<string>('all');
  const [activeTab, setActiveTab] = useState(0);
  const auth = useContext(AuthContext);

  const { data: userCards = [], isLoading: userCardsLoading, isFetching: userCardsFetching } = useQuery<UserCard[]>({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  const recommendationMutation = useMutation({
    mutationFn: (data: { amount: number; category: string; userCardIds: number[]; transaction_location?: 'local' | 'international' }) =>
      transactionService.getSmartRecommendations(data.amount, data.category, data.userCardIds, undefined, undefined, data.transaction_location),
  });

  const [analysisDays, setAnalysisDays] = useState<number>(0); // 0 = all transactions

  const { data: savingsAnalysis, isLoading: savingsLoading, error: savingsError } = useQuery<SavingsAnalysis>({
    queryKey: ['savingsAnalysis', analysisDays],
    queryFn: () => transactionService.getSavingsAnalysis(analysisDays),
    enabled: activeTab === 1,
  });

  const handleGetRecommendations = () => {
    // Don't make request if cards are still loading/fetching (prevents race condition)
    if (userCardsLoading || userCardsFetching) {
      return;
    }
    
    const amountNum = parseFloat(amount);
    // Validate amount is a valid positive number
    if (isNaN(amountNum) || !isFinite(amountNum) || amountNum <= 0) {
      return; // Don't make request if amount is invalid
    }
    // Validate category exists and is not empty
    if (!category || category.trim() === '') {
      return; // Don't make request if category is empty
    }
    // Validate user has cards
    if (!userCards || userCards.length === 0) {
      return; // Don't make request if no cards
    }
    // Validate that all cards have valid IDs
    const cardIds = userCards.map((uc) => uc.card?.id).filter((id) => id != null);
    if (cardIds.length === 0) {
      return; // Don't make request if no valid card IDs
    }
    // All validations passed, make the request
    recommendationMutation.mutate({
      amount: amountNum,
      category: category.trim().toUpperCase(),
      userCardIds: cardIds,
      transaction_location: transactionLocation,
    });
  };

  const recommendation = recommendationMutation.data;
  const filteredAnalysis = rewardPreference === 'all'
    ? recommendation?.all_cards_analysis || []
    : recommendation?.all_cards_analysis.filter((card) => card.reward_type === rewardPreference) || [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
          borderRadius: 4,
          p: 6,
          mb: 5,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)',
              }}
            >
              <SmartToyIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
            <Box>
              <Typography variant="h3" component="h1" fontWeight={900} sx={{ color: 'white', mb: 1 }}>
                Smart Recommendations
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                Get AI-powered card recommendations for maximum rewards
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }} flexWrap="wrap" gap={1}>
            <Chip
              icon={<CreditCardIcon />}
              label={`${userCards.length} Cards Available`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Chip
              icon={<TrendingUpIcon />}
              label="Real-time Analysis"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
          </Stack>
        </Box>
      </Box>

      {/* Tabs */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid rgba(102, 126, 234, 0.1)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 64,
            },
          }}
        >
          <Tab label="Get Recommendation" icon={<SmartToyIcon />} iconPosition="start" />
          <Tab label="Spending Patterns" icon={<AnalyticsIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      {activeTab === 0 && (
        <>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Input Section */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={800} gutterBottom>
                        Purchase Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enter your purchase information for personalized recommendations
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>Purchase Category</InputLabel>
                      <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        label="Purchase Category"
                        sx={{ borderRadius: 2 }}
                      >
                        {CATEGORIES.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* <FormControl fullWidth>
                      <InputLabel>Transaction Location</InputLabel>
                      <Select
                        value={transactionLocation}
                        onChange={(e) => setTransactionLocation(e.target.value as 'local' | 'international')}
                        label="Transaction Location"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="local">Local (TTD)</MenuItem>
                        <MenuItem value="international">International</MenuItem>
                      </Select>
                    </FormControl> */}

                    {/* Category-Specific Conditions Info */}
                    {/* {category && (
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(102, 126, 234, 0.05)',
                          border: '1px solid rgba(102, 126, 234, 0.2)',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'primary.main' }}>
                          {category} Category Information
                        </Typography>
                        {category === 'DINING' && (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.primary', fontWeight: 600 }}>
                              Citi Strata Elite℠ Card Rates:
                            </Typography>
                            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                                <strong>6× points:</strong> Restaurants on Citi Nights℠ (Friday & Saturday 6 PM - 6 AM ET)
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                <strong>3× points:</strong> Restaurants any other time
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'warning.main', fontStyle: 'italic' }}>
                              💡 Tip: Earn 6× points during Citi Nights (Friday/Saturday 6 PM - 6 AM ET) instead of 3×
                            </Typography>
                          </Box>
                        )}
                        {category === 'TRAVEL' && (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.primary', fontWeight: 600 }}>
                              Citi Strata Elite℠ Card Rates:
                            </Typography>
                            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                                <strong>12× points:</strong> Hotels, Car Rentals, and Attractions booked on cititravel.com
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                                <strong>6× points:</strong> Air Travel booked on cititravel.com
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                <strong>1.5× points:</strong> All other travel purchases (default rate)
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'warning.main', fontStyle: 'italic' }}>
                              💡 Tip: Book on cititravel.com to earn 6× or 12× points instead of 1.5×
                            </Typography>
                          </Box>
                        )}
                        {category === 'GROCERIES' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Standard grocery store purchases. Most cards offer consistent rates for groceries.
                            </Typography>
                          </Box>
                        )}
                        {category === 'FUEL' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Gas station and fuel purchases. Some cards offer bonus rates for fuel.
                            </Typography>
                          </Box>
                        )}
                        {category === 'SHOPPING' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Retail and shopping purchases. Rates vary by card and merchant type.
                            </Typography>
                          </Box>
                        )}
                        {category === 'ENTERTAINMENT' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Entertainment purchases including movies, concerts, and streaming services.
                            </Typography>
                          </Box>
                        )}
                        {category === 'UTILITIES' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Utility bill payments. Some cards offer bonus rates for utilities.
                            </Typography>
                          </Box>
                        )}
                        {category === 'ONLINE_SHOPPING' && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Online purchases. Rates may vary based on merchant and card type.
                            </Typography>
                          </Box>
                        )}
                        {!['DINING', 'TRAVEL', 'GROCERIES', 'FUEL', 'SHOPPING', 'ENTERTAINMENT', 'UTILITIES', 'ONLINE_SHOPPING'].includes(category) && (
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Standard purchase category. Reward rates depend on your card's terms.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )} */}

                    <TextField
                      label="Amount (TTD)"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>TT$</Typography>,
                      }}
                      sx={{ borderRadius: 2 }}
                      placeholder="0.00"
                    />

                    <Button
                      variant="contained"
                      onClick={handleGetRecommendations}
                      disabled={!amount || parseFloat(amount) <= 0 || recommendationMutation.isPending || userCards.length === 0 || userCardsLoading || userCardsFetching}
                      fullWidth
                      startIcon={recommendationMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SmartToyIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        py: 1.5,
                        borderRadius: 2,
                        fontSize: '1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {recommendationMutation.isPending ? 'Analyzing...' : 'Get Recommendation'}
                    </Button>

                    {userCards.length === 0 && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          No cards available
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Add cards to your wallet first to get recommendations
                        </Typography>
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Your Cards Section */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f093fb 0%, #4facfe 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CreditCardIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={800} gutterBottom>
                        Your Cards
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userCards.length} card{userCards.length !== 1 ? 's' : ''} in your wallet
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {userCards.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                      }}
                    >
                      <CreditCardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No cards added
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add cards to start getting recommendations
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {userCards.map((uc) => (
                        <Card
                          key={uc.id}
                          sx={{
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'rgba(102, 126, 234, 0.2)',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}
                              >
                                <CreditCardIcon sx={{ fontSize: 20 }} />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {uc.card.name}
                                </Typography>
                                {typeof uc.card === 'object' && uc.card.bank && (
                                  <Typography variant="caption" color="text.secondary">
                                    {typeof uc.card.bank === 'object' ? uc.card.bank.name : 'Unknown Bank'}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recommendation Results */}
          {recommendation && (
            <>
              {/* Top Recommendation Card */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: 4,
                  border: '3px solid',
                  borderColor: 'success.main',
                  boxShadow: '0 20px 60px rgba(16, 185, 129, 0.2)',
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 211, 153, 0.05) 100%)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                  }}
                />
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircleIcon sx={{ color: 'white', fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight={800} gutterBottom>
                        Recommended Card
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Best option for your purchase
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                          {recommendation.recommendation.card_name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {recommendation.recommendation.bank_name}
                        </Typography>
                        <Chip
                          label={recommendation.recommendation.category}
                          sx={{
                            bgcolor: 'success.main',
                            color: 'white',
                            fontWeight: 600,
                            mt: 1,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Amount
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              TT${parseFloat(amount).toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                        {recommendation.recommendation.reward_rate > 0 && (recommendation.recommendation.base_reward > 0 || (recommendation.recommendation.points_earned && recommendation.recommendation.points_earned > 0)) ? (
                          <>
                            <Grid item xs={6}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Reward Rate
                                </Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ color: 'success.main' }}>
                                  {recommendation.recommendation.reward_type === 'miles' 
                                    ? `${recommendation.recommendation.points_earned?.toFixed(0) || (recommendation.recommendation.reward_rate * parseFloat(amount)).toFixed(0)} miles`
                                    : recommendation.recommendation.reward_type === 'points'
                                    ? `${recommendation.recommendation.points_earned?.toFixed(0) || (recommendation.recommendation.reward_rate * parseFloat(amount)).toFixed(0)} points`
                                    : `${recommendation.recommendation.reward_rate}% ${recommendation.recommendation.reward_type}`
                                  }
                                </Typography>
                                {recommendation.recommendation.special_conditions && recommendation.recommendation.special_conditions.length > 0 && (
                                  <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                    {recommendation.recommendation.special_conditions.join(' • ')}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                            {/* Show All Possible Rates if available */}
                            {recommendation.recommendation.all_possible_rates && recommendation.recommendation.all_possible_rates.length > 0 && (
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(255, 193, 7, 0.1)',
                                    border: '1px solid rgba(255, 193, 7, 0.3)',
                                  }}
                                >
                                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'warning.main' }}>
                                    All Possible Rates for {recommendation.recommendation.card_name}
                                  </Typography>
                                  {recommendation.recommendation.all_possible_rates.map((rateInfo, rateIdx) => (
                                    <Typography 
                                      key={rateIdx}
                                      variant="body2" 
                                      sx={{ 
                                        mb: 0.5, 
                                        color: rateInfo.rate === recommendation.recommendation.reward_rate ? 'text.primary' : 'text.secondary',
                                        fontWeight: rateInfo.rate === recommendation.recommendation.reward_rate ? 600 : 400,
                                      }}
                                    >
                                      <strong>{rateInfo.rate}{rateInfo.type === 'cashback' ? '%' : '×'} {rateInfo.type}:</strong> {rateInfo.description}
                                      {rateInfo.rate === recommendation.recommendation.reward_rate && (
                                        <Typography component="span" sx={{ color: 'success.main', ml: 1, fontSize: '0.75rem' }}>
                                          (Current)
                                        </Typography>
                                      )}
                                    </Typography>
                                  ))}
                                </Box>
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: 'white',
                                }}
                              >
                                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5 }}>
                                  Reward Earned
                                </Typography>
                                <Typography variant="h4" fontWeight={800}>
                                  {recommendation.recommendation.reward_type === 'cashback' 
                                    ? `TT$${recommendation.recommendation.base_reward.toFixed(2)}`
                                    : recommendation.recommendation.reward_type === 'points'
                                    ? `${recommendation.recommendation.points_earned?.toFixed(0) || (recommendation.recommendation.reward_rate * parseFloat(amount)).toFixed(0)} points`
                                    : `${recommendation.recommendation.points_earned?.toFixed(0) || (recommendation.recommendation.reward_rate * parseFloat(amount)).toFixed(0)} miles`
                                  }
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: 'rgba(128, 128, 128, 0.1)',
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="body1" color="text.secondary">
                                No Rewards Program
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                This card does not offer rewards, points, or cashback
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Filter and Comparison */}
              <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Reward Type</InputLabel>
                    <Select
                      value={rewardPreference}
                      onChange={(e) => setRewardPreference(e.target.value)}
                      label="Filter by Reward Type"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">All Cards</MenuItem>
                      <MenuItem value="cashback">Cashback Only</MenuItem>
                      <MenuItem value="points">Points Only</MenuItem>
                      <MenuItem value="miles">Miles Only</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              {/* All Cards Comparison */}
              <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight={800} gutterBottom sx={{ mb: 3 }}>
                    All Cards Comparison
                  </Typography>
                  <Stack spacing={2}>
                    {filteredAnalysis.map((card, idx) => (
                      <Card
                        key={idx}
                        sx={{
                          borderRadius: 3,
                          border: '2px solid',
                          borderColor: idx === 0 ? 'success.main' : 'rgba(102, 126, 234, 0.2)',
                          background: idx === 0
                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 211, 153, 0.05) 100%)'
                            : 'white',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                {idx === 0 && (
                                  <StarIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                )}
                                <Typography variant="h6" fontWeight={700}>
                                  {card.card_name}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {card.bank_name}
                              </Typography>
                              {card.reward_rate > 0 && (card.base_reward > 0 || (card.points_earned && card.points_earned > 0)) ? (
                                <>
                                  <Chip
                                    label={
                                      card.reward_type === 'miles'
                                        ? `${card.points_earned?.toFixed(0) || (card.reward_rate * parseFloat(amount)).toFixed(0)} miles`
                                        : card.reward_type === 'points'
                                        ? `${card.points_earned?.toFixed(0) || (card.reward_rate * parseFloat(amount)).toFixed(0)} points`
                                        : `${card.reward_rate}% ${card.reward_type}`
                                    }
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      bgcolor: idx === 0 ? 'success.main' : 'primary.main',
                                      color: 'white',
                                      fontWeight: 600,
                                    }}
                                  />
                                  {card.special_conditions && card.special_conditions.length > 0 && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5, fontStyle: 'italic', fontSize: '0.7rem' }}>
                                      {card.special_conditions.join(' • ')}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Chip
                                  label="No Rewards"
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    bgcolor: 'grey.400',
                                    color: 'white',
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h5" fontWeight={800} sx={{ color: idx === 0 ? 'success.main' : 'primary.main' }}>
                                {card.reward_rate > 0 && (card.base_reward > 0 || (card.points_earned && card.points_earned > 0)) ? (
                                  card.reward_type === 'cashback' 
                                    ? `TT$${card.base_reward.toFixed(2)}`
                                    : card.reward_type === 'points'
                                    ? `${card.points_earned?.toFixed(0) || (card.reward_rate * parseFloat(amount)).toFixed(0)} points`
                                    : `${card.points_earned?.toFixed(0) || (card.reward_rate * parseFloat(amount)).toFixed(0)} miles`
                                ) : 'TT$0.00'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {card.reward_rate > 0 && (card.base_reward > 0 || (card.points_earned && card.points_earned > 0)) ? 'Reward Earned' : 'No Rewards Program'}
                              </Typography>
                            </Box>
                          </Box>
                          {/* Show All Possible Rates if available */}
                          {card.all_possible_rates && card.all_possible_rates.length > 0 && (
                            <Box
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'warning.main' }}>
                                All Possible Rates for {card.card_name}
                              </Typography>
                              <Box sx={{ pl: 1 }}>
                                {card.all_possible_rates.map((rateInfo, rateIdx) => (
                                  <Typography 
                                    key={rateIdx}
                                    variant="body2" 
                                    sx={{ 
                                      mb: 0.5, 
                                      color: rateInfo.rate === card.reward_rate ? 'text.primary' : 'text.secondary',
                                      fontWeight: rateInfo.rate === card.reward_rate ? 600 : 400,
                                    }}
                                  >
                                    <strong>{rateInfo.rate}{rateInfo.type === 'cashback' ? '%' : '×'} {rateInfo.type}:</strong> {rateInfo.description}
                                    {rateInfo.rate === card.reward_rate && (
                                      <Typography component="span" sx={{ color: 'success.main', ml: 1, fontSize: '0.75rem' }}>
                                        (Current)
                                      </Typography>
                                    )}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Date Range Selector */}
          <Card sx={{ mb: 4, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  Analysis Period
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="All Time"
                    onClick={() => setAnalysisDays(0)}
                    sx={{
                      bgcolor: analysisDays === 0 ? 'primary.main' : 'white',
                      color: analysisDays === 0 ? 'white' : 'text.primary',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: analysisDays === 0 ? 'primary.main' : '#e5e7eb',
                      '&:hover': {
                        bgcolor: analysisDays === 0 ? 'primary.dark' : '#f1f5f9',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                  <Chip
                    label="Last 30 Days"
                    onClick={() => setAnalysisDays(30)}
                    sx={{
                      bgcolor: analysisDays === 30 ? 'primary.main' : 'white',
                      color: analysisDays === 30 ? 'white' : 'text.primary',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: analysisDays === 30 ? 'primary.main' : '#e5e7eb',
                      '&:hover': {
                        bgcolor: analysisDays === 30 ? 'primary.dark' : '#f1f5f9',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                  <Chip
                    label="Last 90 Days"
                    onClick={() => setAnalysisDays(90)}
                    sx={{
                      bgcolor: analysisDays === 90 ? 'primary.main' : 'white',
                      color: analysisDays === 90 ? 'white' : 'text.primary',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: analysisDays === 90 ? 'primary.main' : '#e5e7eb',
                      '&:hover': {
                        bgcolor: analysisDays === 90 ? 'primary.dark' : '#f1f5f9',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {savingsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 8, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Loading spending analysis...
              </Typography>
            </Box>
          ) : savingsError ? (
            <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    Error Loading Spending Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {savingsError instanceof Error ? savingsError.message : 'Failed to load spending data'}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          ) : savingsAnalysis ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: 4,
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="h4" fontWeight={800}>
                            TT${Number(savingsAnalysis.total_spent || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Total Spent
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                        {analysisDays === 0 ? 'All transactions' : `Last ${savingsAnalysis.period_days || analysisDays} days`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #4facfe 100%)',
                      color: 'white',
                      borderRadius: 4,
                      boxShadow: '0 8px 24px rgba(240, 147, 251, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(240, 147, 251, 0.4)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <StarIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="h4" fontWeight={800}>
                            TT${Number(savingsAnalysis.total_reward_earned || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Reward Earned
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      color: 'white',
                      borderRadius: 4,
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="h4" fontWeight={800}>
                            TT${Number(savingsAnalysis.total_potential_reward || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Potential Reward
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                      color: 'white',
                      borderRadius: 4,
                      boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(245, 158, 11, 0.4)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <SavingsIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="h4" fontWeight={800}>
                            TT${Number(savingsAnalysis.total_missed_savings || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Missed Savings
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Spending by Category */}
              {savingsAnalysis.by_category && Object.keys(savingsAnalysis.by_category).length > 0 ? (
                <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', mb: 4 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom fontWeight={800} sx={{ mb: 4 }}>
                      Spending by Category
                    </Typography>
                    <Stack spacing={3}>
                      {Object.entries(savingsAnalysis.by_category)
                        .sort(([, a], [, b]) => {
                          const aCat = a as SpendingCategory;
                          const bCat = b as SpendingCategory;
                          return bCat.total_spent - aCat.total_spent;
                        })
                        .map(([categoryCode, cat]) => {
                          const category = cat as SpendingCategory;
                          const percentage = savingsAnalysis.total_spent > 0
                            ? (category.total_spent / savingsAnalysis.total_spent) * 100
                            : 0;
                          return (
                            <Box key={categoryCode}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Box>
                                  <Typography variant="h6" fontWeight={700}>
                                    {category.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {category.transaction_count} transaction{category.transaction_count !== 1 ? 's' : ''} • {percentage.toFixed(1)}% of total
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="h5" fontWeight={800} color="primary">
                                    TT${Number(category.total_spent).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 12,
                                  borderRadius: 6,
                                  bgcolor: '#f1f5f9',
                                  mb: 1.5,
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 6,
                                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                  },
                                }}
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                  label={`Reward: TT$${Number(category.reward_earned || 0).toFixed(2)}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', fontWeight: 600 }}
                                />
                                <Chip
                                  label={`Potential: TT$${Number(category.potential_reward || 0).toFixed(2)}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: 'success.main', fontWeight: 600 }}
                                />
                                {category.missed_savings > 0 && (
                                  <Chip
                                    label={`Missed: TT$${Number(category.missed_savings).toFixed(2)}`}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: 'warning.main', fontWeight: 700 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      No Spending Data Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Upload a credit card statement to see your spending patterns and savings analysis.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => window.location.href = '/upload-statement'}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        textTransform: 'none',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 700,
                      }}
                    >
                      Upload Statement
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Recommended Actions */}
              {savingsAnalysis.recommended_actions && savingsAnalysis.recommended_actions.length > 0 && (
                <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom fontWeight={800} sx={{ mb: 3 }}>
                      Recommended Actions
                    </Typography>
                    <Stack spacing={2}>
                      {savingsAnalysis.recommended_actions.map((action, idx) => (
                        <Card
                          key={idx}
                          sx={{
                            borderRadius: 3,
                            border: '2px solid',
                            borderColor: 'rgba(102, 126, 234, 0.2)',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                  {action.merchant} - {action.category}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Use <strong>{action.recommended_card}</strong> to save TT${Number(action.missed_savings).toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Amount: TT${Number(action.amount).toFixed(2)} • Date: {new Date(action.date).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Chip
                                label={`Save TT$${Number(action.missed_savings).toFixed(2)}`}
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    No Spending Data Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload a credit card statement to see your spending patterns, category breakdown, and potential savings.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => window.location.href = '/upload-statement'}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      textTransform: 'none',
                      mt: 1,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                    }}
                  >
                    Upload Statement
                  </Button>
                </Alert>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Container>
  );
};

export default SmartRecommendations;